<template>
  <div class="flex flex-col relative md:max-w-screen-xl w-full" :class="[iframe ? 'px-2' : '']">
    <div class="hidden md:flex flex-row pb-5" v-if="!iframe">
      <div class="flex flex-row cursor-pointer" @click="backToDashboard">
        <div class="flex items-center">
          <ChevronLeftIcon class="text-brand-green w-5 h-5" />
        </div>
        <div class="flex items-center">
          <p class="text-brand-dark-gray">Back to applicants</p>
        </div>
      </div>
    </div>

    <template v-if="isApplicationLoading">
      <Loading />
    </template>

    <template v-else>
      <template v-if="getCurrentApplication && initialSection">
        <div class="flex md:hidden py-3 px-3 w-full">
          <DropDown
            :options="sectionOptions"
            @select="selectSection"
            :initial="initialSection"
            :showSelectedAsGray="false"
          ></DropDown>
        </div>
      </template>

      <template v-if="getCurrentApplication">
        <!-- START DESKTOP HEADER -->
        <div class="rounded-lg bg-white px-5 py-5 shadow-sm hidden md:flex flex-row" v-if="!iframe">
          <div class="flex flex-col w-1/5">
            <div class="flex items-center">
              <h5 class="text-brand-black text-2xl py-1">
                {{ getCurrentApplication.applicant.name }}
              </h5>
            </div>
            <p class="text-sm py-1">
              {{ getCurrentApplication.unit_address }}
            </p>
          </div>

          <div class="flex flex-row items-center w-1/5 justify-between mx-2 lg:mx-8">
            <div class="flex items-center relative">
              <DownloadIcon
                class="text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green"
                @click="downloadApplicationPackage"
                v-if="!downloading"
                @mouseenter="tooltip.download = true"
                @mouseleave="tooltip.download = false"
              />
              <SmallLoading v-if="downloading" />

              <ToolTip :show="tooltip.download" label="Download application package" />
            </div>

            <div class="flex items-center relative">
              <PaperPlaneIcon
                @click="openShareApplicationModal"
                :classes="'text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green'"
                @mouseenter="tooltip.share = true"
                @mouseleave="tooltip.share = false"
              />
              <ToolTip :show="tooltip.share" label="Share application" />
            </div>

            <div class="flex items-center relative">
              <ClockIcon
                class="text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green"
                @click="openHistoryModal"
                @mouseenter="tooltip.history = true"
                @mouseleave="tooltip.history = false"
              />
              <ToolTip :show="tooltip.history" label="Application history" />
            </div>

            <div class="flex items-center relative">
              <DocumentTextIcon
                @click="openNotesModal"
                :class="[
                  'w-8 h-8 cursor-pointer hover:text-brand-green',
                  getCurrentApplication.notes ? 'text-brand-green' : 'text-brand-dark-gray'
                ]"
                @mouseenter="tooltip.notes = true"
                @mouseleave="tooltip.notes = false"
              />
              <ToolTip :show="tooltip.notes" label="Notes" />
            </div>
          </div>

          <div class="flex flex-row items-center w-1/2 px-5">
            <div v-if="getCurrentApplication.status === 'submitted'" class="flex flex-row w-full">
              <template
                v-if="
                  getCurrentApplication.decisioning_enabled &&
                  getCurrentApplication.show_accept_reject_button
                "
              >
                <div class="mr-3">
                  <SecondaryButton :classes="'border-brand-red'" @click="openRejectModal"
                    ><p class="font-normal text-sm lg:text-base">Decline</p></SecondaryButton
                  >
                </div>
                <div class="">
                  <PrimaryButton @click="openApproveModal"
                    ><p class="font-normal text-white text-xs md:text-xs lg:text-base">
                      Select as tenant
                    </p></PrimaryButton
                  >
                </div>
              </template>
            </div>

            <template v-if="['approved', 'rejected'].includes(getCurrentApplication.status)">
              <div
                class="flex flex-col rounded-lg border-2 px-2 bg-brand-light-gray"
                :class="[
                  getCurrentApplication.status === 'approved'
                    ? 'border-brand-green'
                    : 'border-brand-red'
                ]"
              >
                <p class="py-1 font-semibold">
                  {{ getCurrentApplication.status === "approved" ? "Selected" : "Declined" }}
                  on
                  {{ formatDecisionTime(getCurrentApplication.decision_date) }}
                </p>
                <p class="py-1" v-if="getCurrentUserContext === 'landlord'">
                  by {{ getCurrentApplication.decision_by }}
                </p>
              </div>
            </template>
          </div>

          <div class="flex flex-row items-center border border-brand-mid-gray mx-2 lg:mx-5"></div>

          <div class="flex flex-row items-center lg:px-5">
            <template v-if="getCurrentApplication.is_archived">
              <SecondaryButton @click="unarchiveApplication"
                ><p class="font-normal text-sm lg:text-base">Unarchive</p></SecondaryButton
              >
            </template>
            <template v-else>
              <SecondaryButton @click="archiveApplication"
                ><p class="font-normal text-sm lg:text-base">Archive</p></SecondaryButton
              >
            </template>
          </div>
        </div>
        <!-- END DESKTOP HEADER -->
      </template>

      <div class="flex flex-row">
        <!-- START DESKTOP SIDE NAV -->
        <div class="hidden md:flex flex-col w-1/5 py-5 pr-5" v-if="!iframe">
          <router-link
            :to="{
              name: 'dashboard_view_application_summary',
              params: {
                id: $route.params.id
              }
            }"
            class="py-2"
          >
            <button
              class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
              :class="[
                $route.name === 'dashboard_view_application_summary'
                  ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                  : 'text-brand-black bg-white'
              ]"
            >
              Summary
            </button>
          </router-link>

          <router-link
            :to="{
              name: 'dashboard_view_application_application',
              params: {
                id: $route.params.id
              }
            }"
            class="py-2"
          >
            <button
              class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
              :class="[
                $route.name === 'dashboard_view_application_application'
                  ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                  : 'text-brand-black bg-white'
              ]"
            >
              Application
            </button>
          </router-link>

          <template
            v-if="
              getCurrentApplication &&
              getCurrentApplication.documents &&
              getCurrentApplication.documents.length
            "
          >
            <router-link
              :to="{
                name: 'dashboard_view_application_documents',
                params: {
                  id: $route.params.id
                }
              }"
              class="py-2"
            >
              <button
                class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
                :class="[
                  $route.name === 'dashboard_view_application_documents'
                    ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                    : 'text-brand-black bg-white'
                ]"
              >
                Documents
              </button>
            </router-link>
          </template>

          <template v-if="getCurrentApplication && getCurrentApplication.reports_enabled">
            <router-link
              :to="{
                name: 'dashboard_view_application_report',
                params: {
                  id: $route.params.id,
                  type: profile.report_type
                }
              }"
              v-for="profile in reportProfiles"
              :key="profile.id"
              class="py-2 flex flex-row"
            >
              <button
                class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
                :class="[
                  $route.name === 'dashboard_view_application_report' &&
                  $route.params.type === profile.report_type
                    ? 'border-2 border-brand-green bg-brand-background-light-green'
                    : 'bg-white'
                ]"
              >
                <div class="flex">
                  <p
                    :class="[
                      $route.params.type === profile.report_type
                        ? 'text-brand-green'
                        : pendingStatuses.includes(profile.status)
                        ? 'text-brand-mid-gray'
                        : 'text-brand-black'
                    ]"
                  >
                    {{ profile.short_label }}
                  </p>
                  <div
                    class="flex items-center ml-1"
                    v-if="profile.status.includes('user_action_required')"
                  >
                    <ExclamationIcon class="text-brand-yellow w-5 h-5" />
                  </div>
                  <div
                    class="flex items-center ml-1"
                    v-if="pendingStatuses.includes(profile.status)"
                  >
                    <ClockIcon class="text-brand-mid-gray w-5 h-5" />
                  </div>

                  <div class="flex items-center ml-1" v-if="errorStatuses.includes(profile.status)">
                    <XIcon class="text-brand-red w-5 h-5" />
                  </div>
                </div>
              </button>
            </router-link>
          </template>
        </div>
        <!-- END DESKTOP SIDE NAV -->

        <div class="flex flex-col w-full py-5" :class="[iframe ? 'w-full h-full' : 'md:w-4/5']">
          <router-view
            :key="`${$route.name}-${$route.params.id}${
              $route.params.type ? `-${$route.params.type}` : ''
            }`"
          />
        </div>
      </div>

      <template v-if="getCurrentApplication">
        <router-link
          tag="div"
          :to="{
            name: 'dashboard_view_application_summary',
            params: { id: getCurrentApplication.id }
          }"
          v-if="$route.name !== 'dashboard_view_application_summary'"
          class="md:hidden fixed z-10 left-1 bottom-3 py-2 px-2 rounded bg-brand-green shadow-lg cursor-pointer"
        >
          <div class="flex items-center">
            <ArrowLeftIcon class="text-white w-5 h-5 mr-1" />
            <p class="text-white text-sm">Back to summary</p>
          </div>
        </router-link>
      </template>
    </template>
  </div>

  <template v-if="getCurrentApplication">
    <template v-if="showHistoryModal">
      <ApplicationHistoryModal
        :showModal="showHistoryModal"
        @close="closeHistoryModal"
        :app="getCurrentApplication"
      />
    </template>

    <template v-if="showApproveModal">
      <ApproveModal
        :showModal="showApproveModal"
        @close="closeApproveModal"
        :app="getCurrentApplication"
      />
    </template>

    <template v-if="showRejectModal">
      <RejectModal :showModal="showRejectModal" @close="closeRejectModal" />
    </template>

    <template v-if="showNotesModal">
      <NotesModal :showModal="showNotesModal" @close="closeNotesModal" />
    </template>

    <template v-if="showShareApplicationModal">
      <ShareApplicationModal
        :app="getCurrentApplication"
        :showModal="showShareApplicationModal"
        @close="closeShareApplicationModal"
      />
    </template>
  </template>

  <ActionToast ref="viewApplicationToast" />
</template>
<script>
import { mapGetters } from "vuex"
import moment from "moment"
import _ from "lodash"

import {
  ChevronLeftIcon,
  DownloadIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationIcon,
  ArrowLeftIcon,
  XIcon
} from "@heroicons/vue/outline"

import ApplicationHistoryModal from "./components/ApplicationHistoryModal.vue"
import ApproveModal from "./components/ApproveModal.vue"
import RejectModal from "./components/RejectModal.vue"
import NotesModal from "./components/NotesModal.vue"
import ShareApplicationModal from "./components/ShareApplicationModal.vue"

import {
  APPLICATION_GET,
  APPLICATION_BUNDLE_ARCHIVE,
  APPLICATION_BUNDLE_UNARCHIVE,
  APPLICATION_PACKAGE_DOWNLOAD,
  AUTH_LOGIN
} from "@/store/types"

export default {
  components: {
    ChevronLeftIcon,
    DownloadIcon,
    ClockIcon,
    DocumentTextIcon,
    ExclamationIcon,
    XIcon,
    ArrowLeftIcon,
    ApplicationHistoryModal,
    ApproveModal,
    NotesModal,
    ShareApplicationModal,
    RejectModal
  },
  async mounted() {
    const id = this.$route.params.id

    if (this.$route.query.iframe) {
      this.iframe = this.$route.query.iframe
    }

    if (this.$route.query.backend_token) {
      localStorage.setItem("token", this.$route.query.backend_token)
    }

    if (this.$route.params.token) {
      const shareToken = this.$route.params.token
      await this.$store.dispatch(AUTH_LOGIN, { shareToken })
      this.$router.push({
        name: "dashboard_view_application_summary",
        params: { id }
      })
      return
    }

    if (this.$route.name === "dashboard_view_application") {
      this.$router.push({
        name: "dashboard_view_application_summary",
        params: { id }
      })

      return
    }

    if (!this.getCurrentApplication || this.getCurrentApplication.id !== id) {
      await this.loadApplication()
    }

    if (!this.initialSection) {
      const value = this.$route.name.split("dashboard_view_application_")[1]
      if (["summary", "application", "documents"].includes(value)) {
        this.initialSection = { label: _.capitalize(value), value }
      } else {
        const type = this.$route.params.type
        this.initialSection = {
          label: _.capitalize(type.replace("_", " ")),
          value: type
        }
      }
    }

    const that = this
    this.$emitter.on("dashboard_view_application_approve_modal", (action) => {
      if (action === "open") {
        that.openApproveModal()
      }
    })

    this.$emitter.on("dashboard_view_application_reject_modal", (action) => {
      if (action === "open") {
        that.openRejectModal()
      }
    })

    this.$emitter.on("dashboard_view_application_notes_modal", (action) => {
      if (action === "open") {
        that.openNotesModal()
      }
    })

    this.$emitter.on("dashboard_view_application_application_history_modal", (action) => {
      if (action === "open") {
        that.openHistoryModal()
      }
    })

    this.$emitter.on("dashboard_view_application__modal", (action) => {
      if (action === "open") {
        that.openHistoryModal()
      }
    })

    this.$emitter.on("dashboard_view_application_share_application_modal", (action) => {
      if (action === "open") {
        that.openShareApplicationModal()
      }
    })
  },
  computed: {
    ...mapGetters([
      "getCurrentApplication",
      "getCurrentUser",
      "isApplicationLoading",
      "getCurrentUserContext"
    ]),
    applicationViewLink() {
      return `${location.origin}/dashboard/view/${this.$route.params.id}/application?iframe=true`
    },
    reportProfiles() {
      if (!this.getCurrentUser || !this.getCurrentApplication) return []

      const notOrderedRsp = this.getCurrentUser.report_source_profiles
        .map((rsp) => {
          if (!this.getCurrentApplication.reports.map((r) => r.report_type).includes(rsp.type)) {
            return {
              report_type: rsp.type,
              id: rsp.id,
              short_label: rsp.source_short_label,
              status: ""
            }
          }
        })
        .filter(Boolean)

      const formatted = _.orderBy(
        [...JSON.parse(JSON.stringify(this.getCurrentApplication.reports)), ...notOrderedRsp].map(
          (r) => {
            if (r.report_type === "identity") {
              r.order = 0
              return r
            }

            if (r.report_type === "credit") {
              r.order = 1
              return r
            }
            if (r.report_type === "eviction") {
              r.order = 2
              return r
            }
            if (r.report_type === "criminal") {
              r.order = 3
              return r
            }

            if (r.report_type.includes("employment") || r.report_type.includes("income")) {
              r.order = 4
              return r
            }

            if (r.report_type.includes("landlord")) {
              r.order = 5
              return r
            }
          }
        ),
        ["order"],
        ["asc"]
      ).filter(Boolean)
      return formatted
    },
    sectionOptions() {
      if (!this.getCurrentApplication) {
        return []
      }

      const sectionOptions = [
        { label: "Summary", value: "summary" },
        { label: "Application", value: "application" }
      ]

      if (this.getCurrentApplication.documents && this.getCurrentApplication.documents.length) {
        sectionOptions.push({ label: "Documents", value: "documents" })
      }

      if (this.getCurrentApplication.reports_enabled) {
        this.reportProfiles.map((p) => {
          sectionOptions.push({ label: p.short_label, value: p.report_type })
        })
      }

      return sectionOptions
    }
  },
  data() {
    return {
      iframe: false,
      showHistoryModal: false,
      showApproveModal: false,
      showNotesModal: false,
      showShareApplicationModal: false,
      downloading: false,
      initialSection: null,
      showRejectModal: false,
      pendingStatuses: ["sent_payment_request", "new", "pending", "requested"],
      errorStatuses: ["error", "fatal"],
      tooltip: {}
    }
  },
  methods: {
    formatDecisionTime(date) {
      return moment(date).format("MM/DD/YY @ h:mma")
    },
    async loadApplication() {
      const id = this.$route.params.id
      try {
        await this.$store.dispatch(APPLICATION_GET, { id })
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.$router.push({ name: "403" })
        }
      }
    },
    openShareApplicationModal() {
      this.showShareApplicationModal = true
    },
    async closeShareApplicationModal(reload) {
      this.showShareApplicationModal = false

      if (reload) {
        const id = this.$route.params.id
        await this.$store.dispatch(APPLICATION_GET, {
          id
        })

        this.$refs.viewApplicationToast.show({
          type: "success",
          hasClose: true,
          content: "Application shared",
          icon: "check"
        })
      }
    },
    openNotesModal() {
      this.showNotesModal = true
    },
    closeNotesModal() {
      this.showNotesModal = false
    },
    backToDashboard() {
      if (
        window.history.state.back &&
        window.history.state.back.includes("/dashboard/applicants/")
      ) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: "dashboard_applicants_submitted" })
      }
    },
    openHistoryModal() {
      this.showHistoryModal = true
    },
    closeHistoryModal() {
      this.showHistoryModal = false
    },
    openApproveModal() {
      this.showApproveModal = true
    },
    closeApproveModal(reload) {
      this.showApproveModal = false
      if (reload) {
        this.$refs.viewApplicationToast.show({
          type: "success",
          hasClose: true,
          content: "Applicant approved",
          icon: "check"
        })
        this.loadApplication()
      }
    },
    openRejectModal() {
      this.showRejectModal = true
    },
    closeRejectModal(reload) {
      this.showRejectModal = false
      if (reload) {
        this.$refs.viewApplicationToast.show({
          type: "danger",
          hasClose: true,
          content: "Applicant declined",
          icon: "check"
        })

        this.loadApplication()
      }
    },
    async archiveApplication() {
      const slugs = [this.getCurrentApplication.bundle.slug]

      await this.$store.dispatch(APPLICATION_BUNDLE_ARCHIVE, { slugs })

      this.$refs.viewApplicationToast.show({
        type: "success",
        hasClose: true,
        content: "Applicants archived",
        icon: "check"
      })

      await this.loadApplication()
    },
    async unarchiveApplication() {
      const slugs = [this.getCurrentApplication.bundle.slug]

      await this.$store.dispatch(APPLICATION_BUNDLE_UNARCHIVE, { slugs })
      this.$refs.viewApplicationToast.show({
        type: "success",
        hasClose: true,
        content: "Applicants unarchived",
        icon: "check"
      })

      await this.loadApplication()
    },
    async downloadApplicationPackage() {
      const that = this
      this.downloading = true

      if (this.isApplicationLoading) {
        return
      }

      try {
        const errors = await this.$store.dispatch(APPLICATION_PACKAGE_DOWNLOAD)
        if (errors) {
          this.$refs.viewApplicationToast.show({
            type: "info",
            hasClose: true,
            content:
              "One or more items from this application failed to download successfully. Contact support@rentapplication.net for further assistance.",
            icon: "check"
          })
        } else {
          this.$refs.viewApplicationToast.show({
            type: "success",
            hasClose: true,
            content: "Package downloaded",
            icon: "check"
          })
        }
      } catch (err) {
        setTimeout(() => {
          that.$refs.viewApplicationToast.show({
            type: "error",
            hasClose: true,
            content: "Package download failed",
            icon: "x"
          })
        }, 100)
      }

      this.downloading = false
    },
    selectSection(section) {
      if (["summary", "application", "documents"].includes(section.value)) {
        this.$router.push({
          name: `dashboard_view_application_${section.value}`,
          params: { id: this.getCurrentApplication.id }
        })
      } else {
        this.$router.push({
          name: "dashboard_view_application_report",
          params: { id: this.getCurrentApplication.id, type: section.value }
        })
      }
    }
  }
}
</script>
